import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import PaymentForm from './PaymentForm';
import BillSummaryModal from './BillSummaryModal';
import { isAuthenticatedViaCookie } from '../../utils/utils';

interface Payment {
  id: string;
  amount: number;
  status: string;
  dueDate: string;
  invoiceNumber?: string;
  description?: string;
  payabliToken?: string;
  payabliEntryId?: string;
  currency?: string;
  paymentType?: string;
  createdAt?: string;
  updatedAt?: string;
  statementDate: string;
  paymentDate?: string;
  paymentIdentity?: {
    id: string;
    email: string;
    phone: string | null;
    firstName: string;
    lastName: string;
    uniqueId: string;
    guarantorLastName: string;
  };
  totalDueAmount?: number;
}

interface UserInfo {
  name: string;
  gn: string;
}

interface PaymentAccordionProps {
  payments: Payment[];
  userInfo: UserInfo;
  expandedPaymentId: string | null;
  onPaymentSelect: (payment: Payment) => void;
  onHideDetails: (e: React.MouseEvent) => void;
  onViewBillSummary: (payment: Payment) => void;
  isScheduledPayment: boolean;
  setIsScheduledPayment: (value: boolean) => void;
  scheduleFrequency: string;
  setScheduleFrequency: (value: string) => void;
  splitType: string;
  setSplitType: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  numberOfPayments: number;
  setNumberOfPayments: (value: number) => void;
  amountPerPayment: string;
  setAmountPerPayment: (value: string) => void;
  isScheduledPaymentValid: () => boolean;
  loadPayabliScript: (payment: any) => void;
  getMinStartDate: () => string;
  SCHEDULE_FREQUENCIES: any;
  SPLIT_TYPES: any;
  // BillSummaryModal props
  showBillSummaryModal: boolean;
  billSummaryModalData: {
    accountId: number;
    accountNumber: string;
    guarantorName: string;
    guarantorNumber: string;
    totalAmount: number;
  };
  onCloseBillSummaryModal: () => void;
}

const PaymentAccordion: React.FC<PaymentAccordionProps> = ({
  payments,
  userInfo,
  expandedPaymentId,
  onPaymentSelect,
  onHideDetails,
  onViewBillSummary,
  isScheduledPayment,
  setIsScheduledPayment,
  scheduleFrequency,
  setScheduleFrequency,
  splitType,
  setSplitType,
  startDate,
  setStartDate,
  numberOfPayments,
  setNumberOfPayments,
  amountPerPayment,
  setAmountPerPayment,
  isScheduledPaymentValid,
  loadPayabliScript,
  getMinStartDate,
  SCHEDULE_FREQUENCIES,
  SPLIT_TYPES,
  showBillSummaryModal,
  billSummaryModalData,
  onCloseBillSummaryModal
}) => {
  const { theme } = useTheme();
  const hasAuth = isAuthenticatedViaCookie();
  const isStLuke = theme.client === 'stluke';

  return (
    <div className="mt-6 w-full">
      {/* Table Structure */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden w-full">
        {/* Table Header */}
        <div className={`px-6 py-5 border-b border-slate-200 ${isStLuke ? 'bg-[#F2F5F9]' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm font-semibold text-slate-700">
            <div className="flex items-center">
              <svg className={`w-4 h-4 mr-2 ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Account Number
            </div>
            <div className="flex items-center">
              <svg className={`w-4 h-4 mr-2 ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Statement Date
            </div>
            <div className="flex items-center">
              {/* Use a text $ so it aligns with other header icons */}
              <span className={`inline-flex items-center justify-center w-4 h-4 mr-2 text-sm font-bold leading-none ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`}>$</span>
              Amount
            </div>
            <div className="flex items-center">
              <svg className={`w-4 h-4 mr-2 ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Action
            </div>
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-slate-200">
          {payments.map((payment, index) => (
            <div key={payment.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${isStLuke ? 'hover:bg-slate-100' : 'hover:bg-indigo-50'} transition-colors duration-150`}>
              {/* Table Row */}
              <div className="px-6 py-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                  {/* Account Number */}
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1 sm:hidden">Account Number</span>
                    <div className="text-sm text-slate-800 font-medium truncate">
                      {payment.invoiceNumber ? `#${payment.invoiceNumber}` : `#${payment.id.slice(-8)}`}
                    </div>
                  </div>
                  
                  {/* Statement Date */}
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1 sm:hidden">Statement Date</span>
                    <div className="text-sm text-slate-600">
                      {new Date(payment.statementDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                  
                  {/* Amount */}
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1 sm:hidden">Amount</span>
                    <div className={`text-lg font-bold ${isStLuke ? 'text-[#71001F]' : 'text-blue-600'}`}>
                      ${payment.totalDueAmount?.toLocaleString() || '0.00'}
                    </div>
                  </div>
                  
                  {/* View Details Button */}
                  <div className="flex flex-col">
                    <span className="text-xs text-slate-500 mb-1 sm:hidden">Action</span>
                    {hasAuth ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewBillSummary(payment);
                        }}
                        className={`inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-lg transition-colors duration-200 w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          isStLuke
                            ? 'border-[#71001F] text-[#71001F] bg-white hover:bg-[#F2F5F9] focus:ring-[#71001F]'
                            : 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 focus:ring-blue-500'
                        }`}
                      >
                        View Details
                        <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    ) : (
                      <div className="text-xs text-slate-400 py-2">Login required</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Payment Form */}
              {expandedPaymentId === payment.id && (
                <div className="border-t border-slate-200 bg-slate-50">
                  <div className="px-6 py-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-md font-medium text-slate-800">Payment Details</h4>
                      <button
                        onClick={onHideDetails}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <PaymentForm
                      payment={payment}
                      userInfo={userInfo}
                      isScheduledPayment={isScheduledPayment}
                      setIsScheduledPayment={setIsScheduledPayment}
                      scheduleFrequency={scheduleFrequency}
                      setScheduleFrequency={setScheduleFrequency}
                      splitType={splitType}
                      setSplitType={setSplitType}
                      startDate={startDate}
                      setStartDate={setStartDate}
                      numberOfPayments={numberOfPayments}
                      setNumberOfPayments={setNumberOfPayments}
                      amountPerPayment={amountPerPayment}
                      setAmountPerPayment={setAmountPerPayment}
                      isScheduledPaymentValid={isScheduledPaymentValid}
                      loadPayabliScript={(paymentObj) => loadPayabliScript(paymentObj)}
                      getMinStartDate={getMinStartDate}
                      SCHEDULE_FREQUENCIES={SCHEDULE_FREQUENCIES}
                      SPLIT_TYPES={SPLIT_TYPES}
                      isPayButtonVisible={false}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* BillSummaryModal */}
      {showBillSummaryModal && (
        <BillSummaryModal
          isOpen={showBillSummaryModal}
          onClose={onCloseBillSummaryModal}
          accountId={billSummaryModalData.accountId}
          accountNumber={billSummaryModalData.accountNumber}
          guarantorName={billSummaryModalData.guarantorName}
          guarantorNumber={billSummaryModalData.guarantorNumber}
          totalAmount={billSummaryModalData.totalAmount}
        />
      )}
    </div>
  );
};

export default PaymentAccordion; 